<template>
  <validation-observer ref="observer-question-form">
    <v-container fluid>
      <v-row>
        <v-col cols="12" md="8">
          <validation-provider
            v-slot="{ errors }"
            name="Question Title"
            rules="required|max:255"
          >
            <v-text-field
              outlined
              v-model="title"
              :error-messages="errors"
              counter
              maxlength="255"
              hint="The Question"
              label="Question Text"
              required
              aria-required="true"
              :disabled="readOnly"
            ></v-text-field>
          </validation-provider>
        </v-col>
        <template v-if="allowTypeChange">
          <v-col cols="12" md="4">
            <validation-provider
              v-slot="{ errors }"
              name="Question Type"
              rules="required"
            >
              <v-select
                outlined
                :error-messages="errors"
                :items="questionTypes"
                item-text="text"
                item-value="value"
                label="Question Type"
                v-model="questionType"
                :disabled="readOnly"
              ></v-select>
            </validation-provider>
          </v-col>
        </template>
        <!-- DOB Transition to field -->
        <template v-if="allowDOBTransition">
          <v-col cols="12" md="4">
            <transition-option
              :option="transitionFirstOption"
              @input="changeTransition($event)"
            ></transition-option>
          </v-col>
        </template>
      </v-row>
      <v-row v-if="!readOnly">
        <v-col cols="12" :md="allowMultiTypeTransitions ? 8 : 12">
          <validation-provider
            v-slot="{ errors }"
            name="Question Helper Text"
            rules="max:255"
          >
            <v-text-field
              outlined
              v-model="helperText"
              :error-messages="errors"
              counter
              maxlength="255"
              hint="The question helper text to be displayed"
              label="Question Helper Text"
            ></v-text-field>
          </validation-provider>
        </v-col>
        <v-col cols="12" md="4" v-if="allowMultiTypeTransitions">
          <transition-option
            :option="transitionFirstOption"
            @input="changeTransition($event)"
          ></transition-option>
        </v-col>
        <v-col cols="12" md="6">
          <template>
            <validation-provider v-slot="{ errors }" name="Category">
              <v-select
                outlined
                v-model="questionCategoryId"
                :error-messages="errors"
                :items="questionCategories"
                hint="A category for the Question"
                label="Category"
                required
                aria-required="true"
              ></v-select>
            </validation-provider>
          </template>
        </v-col>
        <v-col cols="12" sm="6">
          <v-combobox
            :value="tags"
            @input="interpretPendingTags($event)"
            :items="getTagsList"
            :disabled="isAddingTag"
            :search-input.sync="tagsSearch"
            hide-selected
            label="Tags"
            multiple
            deletable-chips
            chips
          >
            <template v-slot:no-data>
              <v-list-item v-if="tagsSearch">
                <v-list-item-content>
                  <v-list-item-title>
                    No results matching "<strong>{{ tagsSearch }}</strong
                    >". Press <kbd>enter</kbd> to create a new one
                  </v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </template>
          </v-combobox>
        </v-col>
      </v-row>
    </v-container>
    <component
      :is="questionConfiguration"
      v-model="configuration"
      :questionType="questionType"
      :readOnly="readOnly"
    ></component>
  </validation-observer>
</template>

<script>
import { mapGetters, mapActions, mapState } from "vuex";
import { extend, ValidationObserver, ValidationProvider } from "vee-validate";
import { max, required } from "vee-validate/dist/rules.js";
import QuestionOptions from "@admin/components/surveys_new/QuestionOptions.vue";
import BirthdateOptions from "@admin/components/surveys_new/BirthdateOptions";
import TransitionOption from "@admin/components/surveys_new/TransitionOption.vue";

extend("max", {
  ...max,
  message: "Maximum {length} characters.",
});

extend("required", {
  ...required,
  message: "Please provide a value.",
});

export default {
  name: "QuestionForm",
  data() {
    return {
      tagsSearch: "",
      isAddingTag: false,
      // IMPORTANT: representing tags in this scope is based on computing a property that uses a function getter from vuex.
      // When this is done, reactivity doesn't automatically happen which means the value of tags will not be updated and
      // reflected to the combobox of tags. This is bad since this will mean any newly generated tags will not auto be
      // reflected in the combobox value. In order to make this happen, we need the following incrementing value that is
      // incremented when a tag is added and referenced in the computed property for tags to force reactivity and consequently
      // cause the recomputation of which tags are selected
      tagsIncrement: 0,
      questionTypes: [
        {
          text: "Single Choice",
          value: "single",
        },
        {
          text: "Multiple Choice",
          value: "multiple",
        },
      ],
      questionIdCheck: 0,
    };
  },
  props: {
    value: {
      type: Object,
      required: true,
    },
    readOnly: {
      default: false,
      required: false,
    },
  },
  components: {
    ValidationObserver,
    ValidationProvider,
    QuestionOptions,
    BirthdateOptions,
    TransitionOption,
  },
  watch: {
    questionConfiguration(newValue, oldValue) {
      if (newValue !== oldValue) {
        // If the nested component we'll render to represent question configuration
        // isn't the same as what was previously being loaded, reset configuration.
        // This is done since we expect to provide different configuration options, and we don't
        // want incompatible configuration options that may have been previously stored to be persisted
        // IMPORTANT: there are configuration options managed in this scope (i.e. not via nested components)
        // that should be persisted. The setter for configuration should be written in a way that keeps values for
        // these options while dropping everything else
        this.configuration = {};
      }
      this.questionConfiguration;
    },
  },
  computed: {
    ...mapGetters("tags", ["getTagsList"]),
    ...mapGetters("questionCategories", {
      questionCategories: "getQuestionCategoriesList",
    }),
    ...mapGetters("screenerSurveys", ["tagsPerQuestion"]),
    ...mapState("screenerSurveys", ["screenerSurveyTransitionList"]),
    /**
     * Determine which component should be used to manage the configuration options of a question. The identified
     * value for such a component must be equal to the name of an imported component
     */
    questionConfiguration() {
      switch (this.questionType) {
        case "single":
          return "QuestionOptions";
        case "multiple":
          return "QuestionOptions";
        case "dob":
          return "BirthdateOptions";
        default:
          return null;
      }
    },
    /**
     * Determine if a question should be allowed to have its type changed (which will change how the question operates).
     * We don't want to allow some question types to be changable since we want to force the user to keep the question type
     */
    allowTypeChange() {
      // Don't allow for changing the type of a question if it is birthdate as this is a system question type that cannot be changed.

      return this.value.type !== "dob";
    },
    /**
     * Determine if a question is of type DOB and if so, allow for the transition to be changed
     */
    allowDOBTransition() {
      return this.value.type === "dob";
    },
    title: {
      get() {
        return this.value.title;
      },
      set(value) {
        this.$emit("input", {
          title: value,
          helperText: this.helperText,
          type: this.questionType,
          questionCategoryId: this.questionCategoryId,
          options: this.options,
        });
      },
    },
    helperText: {
      get() {
        return this.value.helperText;
      },
      set(value) {
        this.$emit("input", {
          title: this.title,
          helperText: value,
          type: this.questionType,
          questionCategoryId: this.questionCategoryId,
          options: this.options,
        });
      },
    },
    questionType: {
      get() {
        return this.value.type;
      },
      set(value) {
        this.$emit("input", {
          title: this.title,
          helperText: this.helperText,
          type: value,
          questionCategoryId: this.questionCategoryId,
          options: this.options,
        });
      },
    },
    tags() {
      this.tagsIncrement;
      return this.tagsPerQuestion(this.value.id);
    },
    questionCategoryId: {
      get() {
        return Object.hasOwn(this.value, "questionCategoryId")
          ? this.value.questionCategoryId
          : null;
      },
      set(value) {
        this.$emit("input", {
          title: this.title,
          helperText: this.helperText,
          type: this.questionType,
          questionCategoryId: value,
          options: this.options,
        });
      },
    },

    configuration: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit("input", {
          title: this.title,
          helperText: this.helperText,
          type: this.questionType,
          questionCategoryId: this.questionCategoryId,
          options: value,
        });
      },
    },
    options: {
      get() {
        return this.value.options;
      },
      set(value) {
        this.$emit("input", {
          title: this.title,
          helperText: this.helperText,
          type: this.questionType,
          questionCategoryId: this.questionCategoryId,
          options: value,
        });
      },
    },
    transitionFirstOption() {
      if (this.options.length > 0) {
        return this.options[0];
      }

      return {};
    },

    allowMultiTypeTransitions() {
      return this.questionType === "multiple";
    },
  },
  methods: {
    ...mapActions("screenerSurveys", ["updateTagMap"]),
    ...mapActions("tags", ["addTag"]),
    interpretPendingTags(tags) {
      const promises = [];
      const newTags = [];
      if (this.isAddingTag) {
        return;
      }
      this.isAddingTag = true;
      for (let i = 0; i < tags.length; i++) {
        if (typeof tags[i] === "string") {
          promises.push(this.addTag(tags[i]));
        } else {
          newTags.push(tags[i].value);
        }
      }
      Promise.all(promises).then((results) => {
        for (const result of results) {
          const [success, , data] = result;
          if (success) {
            newTags.push(data.id);
          }
        }

        this.updateTagMap({ questionId: this.value.id, tags: newTags });

        this.tagsIncrement++;
        this.isAddingTag = false;
      });
    },

    changeTransition(event) {
      if (this.options.length > 0) {
        this.options.forEach(function (option) {
          option.transitionTo = event.transitionTo;
          option.transitionTarget = event.transitionTarget;
        });
      }
      this.$emit("input", {
        title: this.title,
        helperText: this.helperText,
        type: this.value.type,
        questionCategoryId: this.questionCategoryId,
        options: this.options,
      });
    },
  },
};
</script>
